import { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import { useLocation } from "react-router";
import { useRecoilState, useRecoilValue } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../TableFilters";

import {
  isAddActionState,
  rowForEditBank,
  isEditActionState,
} from "../atoms/atoms";
import OfficeBankForm from "../form/OfficeBankForm";
import useGetOfficeBank from "../hooks/useGetOfficeBank";
import useDeleteOfficeBank from "../hooks/useDeleteOfficeBank";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import { toast } from "../../../../../components/toastNotification";

export const OfficeBankTable = () => {
  //Data fetching
  const {
    data: bankData,
    isLoading: isFetching,
    refetch: refetchBank,
  } = useGetOfficeBank();

  const {
    mutate: deactivateOfficeBank,
    isSuccess: deacSuccess,
    data: deacResult,
    reset: resetDeacData,
  } = useDeleteOfficeBank();

  const [, setRowForEdit] = useRecoilState(rowForEditBank);

  const [actionEditState, setActionEditState] =
    useRecoilState(isEditActionState);

  const [filteredBank, setFilteredBank] = useState([]);

  //For deactivate checkbox
  const [selectedRows, setSelectedRows] = useState<any>([]);

  //Action state
  const [actionAddState, setActionAddState] = useRecoilState(isAddActionState);

  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  //For search
  const [searchedText, setSearchedText] = useState("");
  const [isFromRedirection, setIsFromRedirection] = useState(false);
  const [bankId, setBankId] = useState("");
  //Permissions
  const addPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_OfficeBank_add;
  const editPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_OfficeBank_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeconfiguration_OfficeBank_delete;
  const userPermissions = useRecoilValue(permissionsSelector);
  const queryClient = useQueryClient();

  //Redirection
  let location = useLocation();
  let recordIdFromRecent;

  if (location.state == null) {
    //Do nothing
  } else {
    recordIdFromRecent = location.state;
  }

  //From recent/notification
  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setBankId(recordIdFromRecent.recordId);
          setFilteredBank(result);
          setIsFromRedirection(true);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, []);

  useEffect(() => {
    setSelectedRows([]);
  }, []);

  const alignCenter = "center" as
    | "center"
    | "inherit"
    | "justify"
    | "left"
    | "right"
    | undefined;

  useEffect(() => {
    if (isFilter && searchedText !== "") {
      const filteredData = bankData?.filter((x: any) =>
        x.bankName
          ?.toLocaleLowerCase()
          ?.trim()
          ?.includes(searchedText?.toLocaleLowerCase()?.trim())
      );
      setFilteredBank(filteredData);
      setIsFilter(false);
      setIsSearching(true);
    }
    if (isSearching && searchedText !== "") {
      const filteredData = bankData?.filter((x: any) =>
        x.bankName
          ?.toLocaleLowerCase()
          ?.trim()
          ?.includes(searchedText?.toLocaleLowerCase()?.trim())
      );
      setFilteredBank(filteredData);
    }

    if (isFromRedirection && bankId !== "") {
      const bankIds = bankId?.split(".")?.map((id) => Number(id?.trim())); // Split, trim, and convert to numbers
      const filteredData = bankData?.filter((x: any) =>
        bankIds?.includes(x.bankId)
      );
      setFilteredBank(filteredData);
      setIsSearching(true);
    }
  }, [isFilter, isSearching, bankData, isFromRedirection, bankId]);

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${
      selectedRows.length
    } ${selectedRows.length > 1 ? "Office Banks " : "Office Bank "}?`}</div>
  );
  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
  };
  const handleBulkDelete = () => {
    const ids = selectedRows?.map((row) => row.bankId);
    deactivateOfficeBank(ids);
    setOpenModalDelete(false);
    setSelectedRows([]);
  };

  if (deacSuccess && deacResult) {
    if (deacResult.status === 201 || deacResult.status === 200) {
      toast.fire(
        "Success",
        "Office Bank were deactivated successfully",
        "success"
      );
      queryClient.invalidateQueries("GetOfficeBank");
    } else {
      toast.fire(
        "Bulk deactivation of Office Bank failed",
        "Server Error: Cannot deactivate Office Bank",
        "error"
      );
      queryClient.invalidateQueries("GetOfficeBank");
    }
    resetDeacData();
  }
  //Columns
  const matColumns = [
    {
      title: "Bank Name",
      field: "bankName",
      customSort: (a, b) =>
        a.bankName && b.bankName
          ? a.bankName.trim().localeCompare(b.bankName.trim())
          : a.bankName
          ? 0
          : -1,
      render: (rowData) => {
        return rowData.bankName;
      },
    },
    {
      title: "Created By",
      field: "createdBy",
      render: (rowData) => {
        return rowData.createdBy;
      },
    },
    {
      title: "Created Date",
      field: "createdDate",
      render: (rowData) => {
        return (
          <>
            {rowData?.createdDate
              ? new Date(rowData.createdDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      render: (rowData) => {
        return rowData.modifiedBy;
      },
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      render: (rowData) => {
        return (
          <>
            {rowData?.modifiedDate
              ? new Date(rowData.modifiedDate).toLocaleDateString()
              : ""}
          </>
        );
      },
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return (
          <div className="font-sans text-left mr-4 text-xs">
            {rowData.isActive ? "Active" : "Inactive"}
          </div>
        );
      },
    },
    {
      title: "Action/s",
      align: alignCenter,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            {userPermissions.includes(editPermission) && (
              <EditButton
                disabled={selectedRows.length > 0}
                className={`${
                  selectedRows.length > 0 ? `opacity-50` : `opacity-100`
                } rounded-full items-center bg-quaternary  text-white hover:bg-hquaternary`}
                onClick={() => {
                  setActionEditState(true);
                  setRowForEdit({ ...rowData });
                  setActionEditState(true);
                }}
              />
            )}
          </div>
        );
      },
    },
  ];
  return (
    <div className="animate-fade-in-down bg-white p-4">
      <div className="">
        {actionAddState || actionEditState ? (
          <div className="">
            <OfficeBankForm
              isAdd={actionAddState}
              setIsAdd={setActionAddState}
              refetchBank={refetchBank}
            />
          </div>
        ) : (
          <>
            <div className="flex justify-between text-xs items-center w-full">
              <TableFilters
                moduleDetails={"Bank Name"}
                textInputName={"BankName"}
                filterText={setSearchedText}
                disabledButton={false}
                setIsSearching={setIsSearching}
                setIsFilter={setIsFilter}
                setIsFromRedirection={setIsFromRedirection}
              />

              <div className="">
                {userPermissions.includes(addPermission) && (
                  <AddButton
                    buttonText="New Bank"
                    onClick={() => setActionAddState(true)}
                    disabled={selectedRows.length > 0}
                  />
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>

            <div className="">
              <MaterialTableTS
                data={isSearching ? filteredBank : bankData}
                showTitle={true}
                columns={matColumns}
                isLoading={isFetching}
                selectedRows={selectedRows}
                setConfirmModalOpen={setOpenModalDelete}
                selection={userPermissions.includes(deletePermission)}
                onSelectionChange={handleSelectedRowsChange}
              />
            </div>
          </>
        )}
        <ConfirmBoxDelete
          ModalTitle="Deactivate Channel"
          qConfirmation={deleteMessage}
          isOpen={openModalDelete}
          onClose={() => setOpenModalDelete(false)}
          onClickFunction={handleBulkDelete}
          cancelButtonRef={undefined}
        />
      </div>
    </div>
  );
};

export default OfficeBankTable;
